// Pure helpers only — no use-api import. The chart components pull these in,
// and use-api drags in auth + portal-config, which throw at module load
// outside a configured page. The fetching hook lives in use-annotations.ts.

// Mirrors the API's AnnotationView (packages/api/src/handlers/api-annotations.ts).
export interface Annotation {
  id: string;
  kind: 'manual' | 'model_change';
  reviewer: string;
  effective_date: string;
  title: string;
  detail?: string;
  url?: string;
  author: string;
  created_at: string;
}

// One vertical line on a chart. Several annotations that land on the same
// plotted date share a line rather than drawing on top of each other.
export interface AnnotationMarker {
  date: string;
  label: string;
  titles: string[];
}

const LABEL_MAX = 24;

// Mirrors MAX_RANGE_DAYS in packages/api/src/handlers/api-annotations.ts: the
// widest to-minus-from span one GET may ask for.
export const ANNOTATION_MAX_RANGE_DAYS = 366;

const DAY_MS = 86_400_000;

function addDays(date: string, n: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);
}

/**
 * Splits inclusive [from, to] into consecutive, non-overlapping inclusive
 * chunks whose span each fits the API's cap. A range already within the cap
 * comes back as the single chunk [from, to].
 */
export function annotationRangeChunks(from: string, to: string): Array<[string, string]> {
  const chunks: Array<[string, string]> = [];
  let start = from;
  while (start <= to) {
    const capped = addDays(start, ANNOTATION_MAX_RANGE_DAYS);
    const end = capped < to ? capped : to;
    chunks.push([start, end]);
    start = addDays(end, 1);
  }
  return chunks;
}

/**
 * Concatenates per-chunk responses (each oldest-first, chunks in date order),
 * so the result stays oldest-first. Deduped on (effective_date, id) — the
 * row's SK identity — not id alone: a model_change id repeats on every date
 * that reviewer changed model.
 */
export function mergeAnnotationPages(pages: Annotation[][]): Annotation[] {
  const seen = new Set<string>();
  const out: Annotation[] = [];
  for (const page of pages) {
    for (const a of page) {
      const key = `${a.effective_date}#${a.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(a);
    }
  }
  return out;
}

/**
 * The annotations that apply to one reviewer's charts: its own plus the
 * fleet-wide '*' ones. `null` is the council view, which shows everything —
 * a single reviewer's model change is exactly what explains a council-level
 * step.
 */
export function annotationsForReviewer(annotations: Annotation[], reviewer: string | null): Annotation[] {
  if (reviewer === null) return annotations;
  return annotations.filter(a => a.reviewer === reviewer || a.reviewer === '*');
}

/**
 * Pins each annotation to a date the chart actually plots.
 *
 * Every chart here has a categorical date axis, and recharts draws a
 * ReferenceLine only at a value that is one of the categories — so a change on
 * a day with no data point (a weekend, a day with no runs, a day trimmed by
 * dropLeadingZeroDays) would silently vanish. Snapping forward to the first
 * plotted date on or after the change puts the line at the first point that
 * can reflect it. An annotation before the first plotted date or after the
 * last is dropped rather than snapped onto an edge it did not happen at.
 *
 * Dates are compared as YYYY-MM-DD strings, never as timestamps.
 */
export function snapAnnotationMarkers(annotations: Annotation[], dates: string[]): AnnotationMarker[] {
  if (dates.length === 0) return [];
  const sorted = [...dates].sort();
  const first = sorted[0]!;
  const byDate = new Map<string, string[]>();
  for (const a of annotations) {
    if (a.effective_date < first) continue;
    const at = sorted.find(d => d >= a.effective_date);
    if (at === undefined) continue;
    const titles = byDate.get(at) ?? [];
    titles.push(a.title);
    byDate.set(at, titles);
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, titles]) => ({
      date,
      titles,
      label: titles.length > 1 ? `${titles.length} changes` : truncate(titles[0]!),
    }));
}

function truncate(s: string): string {
  return s.length <= LABEL_MAX ? s : `${s.slice(0, LABEL_MAX - 1)}…`;
}
